/**
 * Route guard for the signed-in screens.
 *
 * This is a usability measure, never a security boundary — every protected
 * endpoint re-checks the session server-side. Its job is to send an
 * unauthenticated visitor to sign-in while remembering where they meant to go.
 */
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { LoadingScreen } from '@/components/ui';
import { NEXT_PARAM } from '@/lib/redirect';

export function ProtectedRoute() {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Deciding before the session probe settles would flash the sign-in page at
  // users who are in fact signed in.
  if (loading) return <LoadingScreen label="Checking your session" />;

  if (!user) {
    const intended = `${location.pathname}${location.search}`;
    return <Navigate to={`/sign-in?${NEXT_PARAM}=${encodeURIComponent(intended)}`} replace />;
  }

  // An unverified or organization-less account can hold a valid session but
  // must not reach the account screens; the server denies module entry for the
  // same reasons (PRD §6.2, §7.2).
  if (!user.emailVerified) return <Navigate to="/verify-email" replace />;
  if (!user.activeOrganization) return <Navigate to="/awaiting-approval" replace />;

  return <Outlet />;
}
